import {
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  FileText,
  Settings,
  Shield,
  Wallet,
  FileSignature,
  ShieldCheck,
  AlertTriangle,
  Sliders,
  Sparkles,
  BookOpen,
  Database,
  FolderLock,
  Globe,
  Calculator,
  LineChart,
  ShieldAlert,
  Repeat,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { StaticPyramidLogo } from '@/components/landing/StaticPyramidLogo';

const menuItems = [
  { title: 'Dashboard', url: '/dashboard', icon: TrendingUp },
  { title: 'Companies', url: '/companies', icon: Building2 },
  {
    title: 'Transactions',
    url: '/transactions',
    icon: FileText,
    subItems: [
      { title: 'All Transactions', url: '/transactions', icon: FileText },
      { title: 'On-Chain Receipts', url: '/on-chain-receipts', icon: Shield },
    ],
  },
  {
    title: 'Funding',
    url: '/funding/scenarios',
    icon: Calculator,
    subItems: [
      { title: 'Scenarios', url: '/funding/scenarios', icon: Calculator },
      { title: 'Valuation Advisor', url: '/funding/valuation', icon: LineChart },
      { title: 'Share Class Conversion', url: '/share-class-conversion', icon: Repeat },
    ],
  },
  { title: 'Board Portal', url: '/board', icon: FileSignature },
  {
    title: 'Marketplace',
    url: '/liquidity',
    icon: TrendingDown,
    subItems: [
      { title: 'Liquidity', url: '/liquidity', icon: TrendingDown },
      { title: 'Smart Matching', url: '/marketplace/smart-matching', icon: Sparkles },
    ],
  },
  {
    title: 'Compliance',
    url: '/compliance',
    icon: Shield,
    subItems: [
      { title: 'Overview', url: '/compliance', icon: Shield },
      { title: 'Ella 360°', url: '/compliance/ella360', icon: ShieldCheck },
      { title: 'Risk Health', url: '/compliance/risk-health', icon: AlertTriangle },
      { title: 'Risk Settings', url: '/compliance/risk-admin', icon: Sliders },
      { title: 'AI Advisor', url: '/compliance/ella-advisor', icon: Sparkles },
      { title: 'Knowledge Base', url: '/compliance/ella-knowledge', icon: BookOpen },
      { title: 'Learn Admin', url: '/compliance/ella-learn', icon: Database },
      { title: 'Proof Explorer', url: '/compliance/proofs', icon: Shield },
      { title: 'AI Auditor', url: '/compliance/audit-console', icon: ShieldCheck },
      { title: 'Multi-Company Audit', url: '/compliance/multi-company-audit', icon: ShieldCheck },
    ],
  },
  { title: 'Data Room', url: '/data-room', icon: FolderLock },
  {
    title: 'Investor Portal',
    url: '/investor-portal',
    icon: Wallet,
    subItems: [
      { title: 'Overview', url: '/investor-portal', icon: Wallet },
      { title: 'Portfolio Analytics', url: '/investor-portal/portfolio', icon: TrendingUp },
      { title: 'Sensitivity', url: '/investor-portal/sensitivity', icon: Globe },
    ],
  },
  {
    title: 'Settings',
    url: '/settings',
    icon: Settings,
    subItems: [
      { title: 'General', url: '/settings', icon: Settings },
      { title: 'Company Settings', url: '/settings/company', icon: Building2 },
      { title: 'Audit Trail', url: '/audit-trail', icon: FileText },
    ],
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isSuperAdmin } = useSuperAdmin();

  const isActive = (path: string) => currentPath === path;
  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'hover:bg-sidebar-accent/50';

  const collapsed = state === 'collapsed';

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-60'} collapsible="icon">
      <SidebarContent>
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 justify-center">
            {collapsed ? (
              <div className="scale-75">
                <StaticPyramidLogo
                  width={40}
                  height={32}
                  showText={false}
                  showTagline={false}
                  variant="light"
                />
              </div>
            ) : (
              <StaticPyramidLogo
                width={120}
                height={96}
                showText={true}
                showTagline={false}
                variant="light"
              />
            )}
          </div>
        </div>

        <SidebarGroup>
          <SidebarGroupLabel>Main Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>

                  {item.subItems && !collapsed && (
                    <SidebarMenuSub>
                      {item.subItems.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink to={subItem.url} end className={getNavCls}>
                              <subItem.icon className="h-4 w-4" />
                              <span>{subItem.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              ))}

              {isSuperAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/super-admin" end className={getNavCls}>
                      <ShieldAlert className="h-4 w-4" />
                      {!collapsed && <span>Super Admin</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
